import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import "./themeUpdate.css";

const VALID_GIF_EXT = ["gif"];
const VALID_PNG_EXT = ["png"];
const CHARACTER_MAX_FILE_MB = 2;
const MAX_GOOD_BUGS = 5;
const MAX_BAD_BUGS = 10;
const MIN_GOOD_BUGS = 1;
const MIN_BAD_BUGS = 1;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const parseBugArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const createEmptyBug = (type) => ({
  bug_gif: "",
  death_image: "",
  bug_type: "flying",
  points: type === "good" ? -5 : 10,
  speed: 2,
  spawn_rate: 1,
});

const buildHostedAssetUrl = (baseEnv, relativePath) => {
  const base = String(baseEnv ?? "").replace(/\/+$/, "");
  const raw = String(relativePath ?? "").trim().replace(/^\/+/, "");
  if (!raw) return "";
  const encoded = raw
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg));
      } catch {
        return encodeURIComponent(seg);
      }
    })
    .join("/");
  return base ? `${base}/${encoded}` : encoded;
};

const renameFileForUpload = (file) => {
  if (!file || !(file instanceof Blob)) return file;
  const original = file.name || "image";
  const lastDot = original.lastIndexOf(".");
  const ext = lastDot >= 0 ? original.slice(lastDot) : "";
  const base = lastDot >= 0 ? original.slice(0, lastDot) : original;
  const id = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  const newName = `${id}-${base}${ext}`;
  return new File([file], newName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified,
  });
};

const normalizeMissingValue = (v) => String(v ?? "").trim();

const findMissingThemeImages = (themeData) => {
  const td = themeData || {};
  const missing = [];
  ([
    { key: "themeImage", label: "Thumbnail image" },
    { key: "logo", label: "Logo image" },
    { key: "background_desk", label: "Desktop background" },
    { key: "background_mob", label: "Mobile background" },
  ]).forEach(({ key, label }) => {
    if (!normalizeMissingValue(td[key])) missing.push(`Theme: upload ${label}`);
  });
  return missing;
};

function ThemeUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [goodBugs, setGoodBugs] = useState([]);
  const [badBugs, setBadBugs] = useState([]);
  const [uploadFilesByToken, setUploadFilesByToken] = useState({});
  const [saving, setSaving] = useState(false);
  const warnedMissingKeyRef = useRef("");

  const localAssets = useMemo(
    () => require.context("../../../img", false, /\.(png|jpg|jpeg|gif)$/),
    []
  );

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const nextGood = parseBugArray(data.good);
    const nextBad = parseBugArray(data.bad);
    setGoodBugs(
      nextGood.length
        ? nextGood.map((item) => ({
            bug_gif: item?.bug_gif || "",
            death_image: item?.death_image || "",
            bug_type: item?.bug_type || item?.type || "flying",
            points: Number(item?.points ?? -5),
            speed: Number(item?.speed ?? 2),
            spawn_rate: Number(item?.spawn_rate ?? 1),
          }))
        : [createEmptyBug("good")]
    );
    setBadBugs(
      nextBad.length
        ? nextBad.map((item) => ({
            bug_gif: item?.bug_gif || "",
            death_image: item?.death_image || "",
            bug_type: item?.bug_type || item?.type || "walking",
            points: Number(item?.points ?? 10),
            speed: Number(item?.speed ?? 2),
            spawn_rate: Number(item?.spawn_rate ?? 1),
          }))
        : [createEmptyBug("bad")]
    );
  }, [data]);

  useEffect(() => {
    if (!currentTheme || !data) return;
    const missing = findMissingThemeImages(data);
    if (!missing.length) return;
    const key = `${currentTheme}::${missing.join("|")}`;
    if (warnedMissingKeyRef.current === key) return;
    warnedMissingKeyRef.current = key;
    void Swal.fire({
      icon: "warning",
      title: "Missing theme images",
      html: `<div style="text-align:left">This theme is missing required images. Please add them before playing:<ul style="margin:10px 0 0 18px">${missing
        .map((m) => `<li>${String(m).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</li>`)
        .join("")}</ul></div>`,
    });
  }, [currentTheme, data]);

  const uploadBlobSrcByToken = useMemo(() => {
    const next = {};
    Object.entries(uploadFilesByToken).forEach(([token, file]) => {
      if (file) next[token] = URL.createObjectURL(file);
    });
    return next;
  }, [uploadFilesByToken]);

  useEffect(
    () => () => {
      Object.values(uploadBlobSrcByToken).forEach((url) =>
        URL.revokeObjectURL(url)
      );
    },
    [uploadBlobSrcByToken]
  );

  const getBugFieldBounds = (type, field) => {
    // Note: "good" points are negative; "bad" points are positive.
    if (field === "speed" || field === "spawn_rate") return { lo: 1, hi: 10 };
    if (field === "points") {
      return type === "good" ? { lo: -100, hi: -1 } : { lo: 1, hi: 100 };
    }
    return { lo: -Infinity, hi: Infinity };
  };

  const getPreviewUrl = (fileName) => {
    if (!fileName) return "";
    try {
      return localAssets(`./${fileName}`);
    } catch {
      return buildHostedAssetUrl(process.env.REACT_APP_S3_PATH || "", fileName);
    }
  };

  const validateImageFile = (file, allowedExt) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!allowedExt.includes(ext))
      return `Unsupported format for ${file.name}`;
    if (file.size > CHARACTER_MAX_FILE_MB * 1024 * 1024)
      return `${file.name} exceeds ${CHARACTER_MAX_FILE_MB}MB`;
    return null;
  };

  const validateImageDimensions200 = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      const cleanup = () => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          /* ignore */
        }
      };
      img.onload = () => {
        const ok = img.naturalWidth === 200 && img.naturalHeight === 200;
        cleanup();
        resolve(
          ok
            ? null
            : "Please upload file with dimension 200x200 px "
        );
      };
      img.onerror = () => {
        cleanup();
        resolve(`Could not read ${file.name}. Please upload a valid GIF/PNG.`);
      };
      img.src = url;
    });

  const validateBugMediaFile = async (field, file) => {
    const allowedExt = field === "bug_gif" ? VALID_GIF_EXT : VALID_PNG_EXT;
    const basic = validateImageFile(file, allowedExt);
    if (basic) return basic;
    return await validateImageDimensions200(file);
  };

  const isUploadToken = (value) =>
    typeof value === "string" && value.startsWith("__UPLOAD__:");

  const getTokenFromValue = (value) =>
    isUploadToken(value) ? value.replace("__UPLOAD__:", "") : null;

  const getImageSrcFromValue = (value) => {
    const token = getTokenFromValue(value);
    if (token) return uploadBlobSrcByToken[token] || "";
    return getPreviewUrl(value);
  };

  const updateBugField = (type, index, field, value) => {
    const setter = type === "good" ? setGoodBugs : setBadBugs;
    const clampBugNumeric = (rawValue) => {
      // Keep non-numeric intermediate values while typing (e.g. "-" / "").
      const n =
        typeof rawValue === "number" ? rawValue : parseFloat(String(rawValue));
      if (!Number.isFinite(n)) return rawValue;

      const { lo, hi } = getBugFieldBounds(type, field);
      return clamp(n, lo, hi);
    };
    setter((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: clampBugNumeric(value) } : item
      )
    );
  };

  const coerceBugFieldToBounds = (type, index, field, rawValue) => {
    const setter = type === "good" ? setGoodBugs : setBadBugs;
    const { lo, hi } = getBugFieldBounds(type, field);

    const n =
      typeof rawValue === "number" ? rawValue : parseFloat(String(rawValue));

    const safe =
      Number.isFinite(n) ? clamp(n, lo, hi) : // If invalid/empty, coerce to min
        lo;

    setter((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: safe } : item))
    );
  };

  const onPickBugMedia = async (type, index, field, file) => {
    if (!file) return;
    const error = await validateBugMediaFile(field, file);
    if (error) {
      Swal.fire("Invalid file", error, "error");
      return;
    }
    const renamed = renameFileForUpload(file);
    const token = `${type}_${field}_${index}_${Date.now()}`;
    setUploadFilesByToken((prev) => ({ ...prev, [token]: renamed }));
    updateBugField(type, index, field, `__UPLOAD__:${token}`);
  };

  const addBugRow = (type) => {
    const list = type === "good" ? goodBugs : badBugs;
    const max = type === "good" ? MAX_GOOD_BUGS : MAX_BAD_BUGS;
    if (list.length >= max) {
      Swal.fire(
        "Limit reached",
        `Maximum ${max} ${type} bugs allowed.`,
        "warning"
      );
      return;
    }
    const setter = type === "good" ? setGoodBugs : setBadBugs;
    setter((prev) => [...prev, createEmptyBug(type)]);
  };

  const removeBugRow = (type, index) => {
    const list = type === "good" ? goodBugs : badBugs;
    const min = type === "good" ? MIN_GOOD_BUGS : MIN_BAD_BUGS;
    if (list.length <= min) {
      void Swal.fire({
        icon: "info",
        title: "At least one bug required",
        text:
          type === "good"
            ? "You must keep at least one good bug."
            : "You must keep at least one bad bug.",
      });
      return;
    }
    const setter = type === "good" ? setGoodBugs : setBadBugs;
    setter((prev) => prev.filter((_, i) => i !== index));
  };

  const collectReferencedUploadTokens = (rows) => {
    const tokens = [];
    rows.forEach((row) => {
      ["bug_gif", "death_image"].forEach((key) => {
        const token = getTokenFromValue(row?.[key]);
        if (token) tokens.push(token);
      });
    });
    return tokens;
  };

  const validateMissingBugMedia = (rows, typeLabel) => {
    const missing = [];
    rows.forEach((row, idx) => {
      const rowLabel = `${typeLabel} bug ${idx + 1}`;
      ([
        { key: "bug_gif", label: "Bug GIF" },
        { key: "death_image", label: "Death image (PNG)" },
      ]).forEach(({ key, label }) => {
        const val = String(row?.[key] ?? "").trim();
        if (!val) {
          missing.push(`${rowLabel}: upload ${label}`);
          return;
        }
        const token = getTokenFromValue(val);
        if (token && !uploadFilesByToken[token]) {
          // The row points to an upload token, but the file is missing from state.
          missing.push(`${rowLabel}: re-upload ${label}`);
        }
      });
    });
    return missing;
  };

  const handleSave = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      const missingThemeImages = findMissingThemeImages(data);
      if (missingThemeImages.length) {
        setSaving(false);
        Swal.fire({
          icon: "warning",
          title: "Missing theme images",
          html: `<div style="text-align:left">Please upload the missing theme images before saving/playing:<ul style="margin:10px 0 0 18px">${missingThemeImages
            .map((m) => `<li>${String(m).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</li>`)
            .join("")}</ul></div>`,
        });
        return;
      }
      const normalizeBugRow = (bug, type) => {
        // Use parseFloat so '' becomes NaN (not 0), then coerce NaN to min bounds.
        const points = parseFloat(String(bug?.points ?? ""));
        const speed = parseFloat(String(bug?.speed ?? ""));
        const spawnRate = parseFloat(String(bug?.spawn_rate ?? ""));
        const pointsSafe =
          type === "good" ? { lo: -100, hi: -1 } : { lo: 1, hi: 100 };
        const speedSafe = { lo: 1, hi: 10 };

        return {
          ...bug,
          points: Number.isFinite(points)
            ? clamp(points, pointsSafe.lo, pointsSafe.hi)
            : pointsSafe.lo,
          speed: Number.isFinite(speed)
            ? clamp(speed, speedSafe.lo, speedSafe.hi)
            : speedSafe.lo,
          spawn_rate: Number.isFinite(spawnRate)
            ? clamp(spawnRate, speedSafe.lo, speedSafe.hi)
            : speedSafe.lo,
        };
      };
      const normalizedGood = goodBugs.filter(Boolean).map((b) => normalizeBugRow(b, "good"));
      const normalizedBad = badBugs.filter(Boolean).map((b) => normalizeBugRow(b, "bad"));

      const missingMedia = [
        ...validateMissingBugMedia(normalizedGood, "Good"),
        ...validateMissingBugMedia(normalizedBad, "Bad"),
      ];
      if (missingMedia.length) {
        setSaving(false);
        Swal.fire({
          icon: "warning",
          title: "Missing uploads",
          html: `<div style="text-align:left">Please upload the missing images/gifs before saving:<ul style="margin:10px 0 0 18px">${missingMedia
            .map((m) => `<li>${String(m).replace(/</g, "&lt;").replace(/>/g, "&gt;")}</li>`)
            .join("")}</ul></div>`,
        });
        return;
      }

      formData.append("themeName", currentTheme);
      formData.append(
        "extraData",
        JSON.stringify({
          themeName: data?.themeName || "",
          themeDescription: data?.themeDescription || "",
          good: normalizedGood,
          bad: normalizedBad,
          timer: data?.timer || "05:00",
        })
      );
      const uploadTokens = [
        ...collectReferencedUploadTokens(normalizedGood),
        ...collectReferencedUploadTokens(normalizedBad),
      ];
      uploadTokens.forEach((token) => {
        const file = uploadFilesByToken[token];
        if (file) formData.append(token, file);
      });
      await uploadThemeMedia({ formData, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setUploadFilesByToken({});
      Swal.fire("Success", "Theme data updated", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.message || "Failed to update theme data",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const renderBugSection = (type, bugs) => {
    const max = type === "good" ? MAX_GOOD_BUGS : MAX_BAD_BUGS;
    const bugLabel = type === "good" ? "Good Bug" : "Bad Bug";
    const titleText = type === "good" ? "✅ Good Bugs" : "❌ Bad Bugs";

    return (
      <section className="tu-section-card">
        {/* Section header */}
        <div className="tu-section-header">
          <h3 className={`tu-section-title ${type}`}>{titleText}</h3>
          <button className="tu-add-btn" onClick={() => addBugRow(type)}>
            + Add {bugLabel}
          </button>
        </div>
        <p className="tu-helper-text">
          {bugs.length}/{max} {type} bugs
        </p>

        {/* Bug cards */}
        {bugs.map((item, index) => (
          <div className="tu-bug-card" key={`${type}-${index}`}>
            {/* Card header */}
            <div className="tu-bug-card-header">
              <div className="tu-bug-card-title-row">
                <div className={`tu-badge ${type}`}>{index + 1}</div>
                <span className="tu-bug-label">
                  {bugLabel} {index + 1}
                </span>
              </div>
              <button
                type="button"
                className="tu-remove-btn"
                disabled={bugs.length <= (type === "good" ? MIN_GOOD_BUGS : MIN_BAD_BUGS)}
                title={
                  bugs.length <= (type === "good" ? MIN_GOOD_BUGS : MIN_BAD_BUGS)
                    ? `At least ${type === "good" ? MIN_GOOD_BUGS : MIN_BAD_BUGS} ${type} bug required`
                    : "Remove this bug"
                }
                onClick={() => removeBugRow(type, index)}
              >
                ✕ Remove
              </button>
            </div>

            {/* Images */}
            <div className="tu-images-row">
              {/* Bug GIF */}
              <div className="tu-image-block">
                <span className="tu-image-label">Bug Image / GIF</span>
                <div className="tu-image-preview-box">
                  {getImageSrcFromValue(item.bug_gif) ? (
                    <img
                      src={getImageSrcFromValue(item.bug_gif)}
                      alt={`${type}-bug-${index}`}
                    />
                  ) : (
                    <span className="tu-image-placeholder">No image</span>
                  )}
                </div>
                <span className="tu-file-hint">
                  File dimension: 200×200 px (GIF only)
                </span>
                <input
                  type="file"
                  accept=".gif"
                  className="tu-file-input"
                  onChange={(e) =>
                    onPickBugMedia(type, index, "bug_gif", e.target.files?.[0])
                  }
                />
              </div>

              {/* Death image */}
              <div className="tu-image-block">
                <span className="tu-image-label">Death Image</span>
                <div className="tu-image-preview-box">
                  {getImageSrcFromValue(item.death_image) ? (
                    <img
                      src={getImageSrcFromValue(item.death_image)}
                      alt={`${type}-death-${index}`}
                    />
                  ) : (
                    <span className="tu-image-placeholder">No image</span>
                  )}
                </div>
                <span className="tu-file-hint">
                  File dimension: 200×200 px (PNG only)
                </span>
                <input
                  type="file"
                  accept=".png"
                  className="tu-file-input"
                  onChange={(e) =>
                    onPickBugMedia(
                      type,
                      index,
                      "death_image",
                      e.target.files?.[0]
                    )
                  }
                />
              </div>
            </div>

            {/* Numeric fields */}
            <div className="tu-fields-row">
              <div className="tu-field-block">
                <label className="tu-field-label">Type</label>
                <select
                  className="tu-field-input"
                  value={item.bug_type || "flying"}
                  onChange={(e) => updateBugField(type, index, "bug_type", e.target.value)}
                >
                  <option value="flying">Flying (bee / mosquito / butterfly)</option>
                  <option value="walking">Walking/Crawling (ant / worm / cockroach)</option>
                </select>
              </div>
              <div className="tu-field-block">
                <label className="tu-field-label">Points</label>
                <input
                  type="number"
                  className="tu-field-input"
                  value={item.points}
                  placeholder="points"
                  min={type === "good" ? -100 : 1}
                  max={type === "good" ? -1 : 100}
                  onChange={(e) =>
                    updateBugField(type, index, "points", e.target.value)
                  }
                  onBlur={(e) =>
                    coerceBugFieldToBounds(type, index, "points", e.target.value)
                  }
                />
              </div>
              <div className="tu-field-block">
                <label className="tu-field-label">Speed</label>
                <input
                  type="number"
                  step="0.1"
                  className="tu-field-input"
                  value={item.speed}
                  placeholder="speed"
                  min={1}
                  max={10}
                  onChange={(e) =>
                    updateBugField(type, index, "speed", e.target.value)
                  }
                  onBlur={(e) =>
                    coerceBugFieldToBounds(type, index, "speed", e.target.value)
                  }
                />
              </div>
              <div className="tu-field-block">
                <label className="tu-field-label">Spawn Rate</label>
                <input
                  type="number"
                  step="0.1"
                  className="tu-field-input"
                  value={item.spawn_rate}
                  placeholder="spawn rate"
                  min={1}
                  max={10}
                  onChange={(e) =>
                    updateBugField(
                      type,
                      index,
                      "spawn_rate",
                      e.target.value
                    )
                  }
                  onBlur={(e) =>
                    coerceBugFieldToBounds(
                      type,
                      index,
                      "spawn_rate",
                      e.target.value
                    )
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </section>
    );
  };

  return (
    <div className="tu-page">
      {/* Back */}
      <button className="tu-back-btn" onClick={() => navigate("/admin/rules")}>
        ← Back
      </button>

      {/* Page title */}
      <div className="tu-header">
        <h2>Theme Bug Editor</h2>
        <p>Configure good and bad bugs for the current theme</p>
      </div>

      {/* Two-column sections */}
      <div className="tu-grid">
        {renderBugSection("good", goodBugs)}
        {renderBugSection("bad", badBugs)}
      </div>

      {/* Save */}
      <div className="tu-actions">
        <button
          className="tu-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

export default ThemeUpdate;
